import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { 
  X, Search, FileText, Eye, Upload, Link2, Unlink, Plus,
  CheckCircle2, AlertCircle, Users, CreditCard, ExternalLink,
  ChevronRight, Sparkles, Loader2
} from "lucide-react";

const STATUT_LABELS = {
  non_rapproche: { label: "Non rapproché", color: "bg-slate-100 text-slate-700" },
  en_attente_validation: { label: "À valider", color: "bg-orange-100 text-orange-700" },
  rapproche: { label: "Rapproché", color: "bg-green-100 text-green-700" },
  rapproche_auto: { label: "Rapproché auto", color: "bg-green-100 text-green-700" },
  rapproche_manuel: { label: "Rapproché manuel", color: "bg-blue-100 text-blue-700" },
  rapproche_groupe: { label: "Paiement groupé", color: "bg-indigo-100 text-indigo-700" },
  rapproche_remise_sepa: { label: "Remise SEPA", color: "bg-purple-100 text-purple-700" },
  divers: { label: "Traité", color: "bg-purple-100 text-purple-700" },
  ignore: { label: "Ignoré", color: "bg-red-100 text-red-700" },
  sans_correspondance: { label: "Sans correspondance", color: "bg-yellow-100 text-yellow-700" },
};

/**
 * Panneau latéral de détail style Pennylane
 */
export function TransactionDetailSheet({
  transaction,
  remiseDetail,
  factureDetail,
  loadingDetail,
  suggestions = [],
  onClose,
  onOpenMatchModal,
  onViewPdf,
  onUnlink,
  onValidateSuggestion,
  formatCurrency,
  formatDate,
  formatDateLong,
}) {
  const [validating, setValidating] = useState(null);
  
  if (!transaction) return null;

  const statut = STATUT_LABELS[transaction.statut_rapprochement] || STATUT_LABELS.non_rapproche;
  const isCredit = transaction.amount > 0;
  const isRapprochee = ['rapproche', 'rapproche_auto', 'rapproche_manuel', 'rapproche_remise_sepa', 'rapproche_groupe'].includes(transaction.statut_rapprochement);
  const canMatch = !isRapprochee && transaction.statut_rapprochement !== 'divers' && transaction.statut_rapprochement !== 'ignore';
  
  // Détecter si c'est une transaction REM SDD
  const desc = (transaction.clean_description || transaction.provider_description || '').toLowerCase();
  const isRemSdd = desc.includes('rem sdd') || desc.includes('rem. sdd') || transaction.statut_rapprochement === 'rapproche_remise_sepa';

  const handleValidate = async (facture) => {
    setValidating(facture.numero);
    await onValidateSuggestion?.(facture);
    setValidating(null);
  };

  return (
    <div className="h-full flex flex-col bg-white border-l" data-testid="transaction-detail-sheet">
      {/* Header compact */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
        <span className="text-xs text-slate-500">Détail transaction</span>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
          <X className="h-3 w-3" />
        </Button>
      </div>

      {/* Montant principal - compact */}
      <div className="px-3 py-3 border-b">
        <p className={`text-2xl font-bold ${isCredit ? 'text-green-600' : 'text-slate-900'}`}>
          {isCredit ? '+' : ''}{formatCurrency(transaction.amount)}
        </p>
        <p className="text-xs text-slate-600 mt-1 font-medium truncate">
          {transaction.clean_description || transaction.provider_description}
        </p>
        <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
          <span>{formatDate(transaction.date)}</span>
          <span className="flex items-center gap-1">
            <CreditCard className="w-3 h-3" />
            {transaction.account_name || 'Compte'}
          </span>
        </div>
      </div>

      {/* Contenu scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Section Justificatif - Affiché en PREMIER */}
        <div className="px-3 py-3 border-b">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-700">Justificatif</span>
          </div>

          {/* Affichage selon le statut */}
          {loadingDetail ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
            </div>
          ) : remiseDetail ? (
            /* Détail Remise SEPA - Compact */
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-purple-700 font-medium text-sm">
                  <Users className="w-3.5 h-3.5" />
                  Remise SEPA
                </div>
                <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0">
                  {remiseDetail.nb_prelevements} clients
                </Badge>
              </div>
              
              {/* Date d'exécution */}
              {remiseDetail.date_execution && (
                <p className="text-[10px] text-slate-500">
                  Exécution : <span className="font-medium">{formatDate(remiseDetail.date_execution)}</span>
                </p>
              )}
              
              {/* Stats compactes */}
              <div className="grid grid-cols-3 gap-1 text-center">
                <div className="bg-green-50 rounded p-1.5 border border-green-200">
                  <p className="text-sm font-bold text-green-600">{remiseDetail.nb_rapproches || 0}</p>
                  <p className="text-[9px] text-slate-500">Liées</p>
                </div>
                <div className="bg-orange-50 rounded p-1.5 border border-orange-200">
                  <p className="text-sm font-bold text-orange-600">
                    {(remiseDetail.nb_prelevements || 0) - (remiseDetail.nb_rapproches || 0)}
                  </p>
                  <p className="text-[9px] text-slate-500">Non liées</p>
                </div>
                <div className="bg-blue-50 rounded p-1.5 border border-blue-200">
                  <p className="text-sm font-bold text-blue-600">{formatCurrency(remiseDetail.montant_total)}</p>
                  <p className="text-[9px] text-slate-500">Total</p>
                </div>
              </div>

              {/* Liste compacte des prélèvements */}
              <div className="border rounded overflow-hidden">
                <div className="bg-slate-50 px-2 py-1 border-b">
                  <p className="text-[10px] font-medium text-slate-600">Prélèvements ({remiseDetail.nb_prelevements})</p>
                </div>
                <div className="max-h-[180px] overflow-y-auto divide-y">
                  {remiseDetail.prelevements?.map((p, idx) => {
                    const isRapproche = p.statut === 'rapproche' || p.statut === 'rapproche_manuel' || p.facture_numero;
                    return (
                      <div 
                        key={p.id || idx}
                        className={`px-2 py-1.5 ${isRapproche ? 'bg-green-50/50' : 'bg-white'}`}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <div className="flex items-center gap-1 flex-1 min-w-0">
                            {isRapproche ? (
                              <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                            ) : (
                              <AlertCircle className="w-3 h-3 text-orange-500 flex-shrink-0" />
                            )}
                            <span className="text-xs truncate">{p.nom_client}</span>
                          </div>
                          <span className={`text-xs font-semibold whitespace-nowrap ${isRapproche ? 'text-green-600' : 'text-slate-700'}`}>
                            {formatCurrency(p.montant)}
                          </span>
                        </div>
                        {p.facture_numero && (
                          <div className="flex items-center gap-1 ml-4">
                            <span className="text-green-600 text-[10px] font-medium">
                              {p.facture_numero}
                            </span>
                            {p.has_pdf && (
                              <button
                                onClick={() => onViewPdf?.(p.facture_numero, 'vente')}
                                className="text-blue-600 text-[10px] hover:underline flex items-center gap-0.5"
                              >
                                <Eye className="w-2.5 h-2.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : factureDetail || transaction.facture_rapprochee_numero ? (
            /* Facture liée */
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start justify-between">
                <div>
                  <button
                    onClick={() => onViewPdf?.(transaction.facture_rapprochee_numero, transaction.facture_rapprochee_type)}
                    className="text-blue-600 font-bold hover:underline flex items-center gap-1"
                  >
                    <FileText className="w-4 h-4" />
                    {transaction.facture_rapprochee_numero}
                    <Eye className="w-3 h-3 ml-1 opacity-50" />
                  </button>
                  <p className="text-sm text-slate-600 mt-1">
                    {factureDetail?.client?.nom || factureDetail?.fournisseur || '-'}
                  </p>
                  <p className="text-xs text-slate-400">
                    {formatDate(factureDetail?.date)}
                  </p>
                </div>
                <p className="text-lg font-bold">{formatCurrency(factureDetail?.total_ttc)}</p>
              </div>
            </div>
          ) : transaction.factures_liees && transaction.factures_liees.length > 0 ? (
            /* Paiement groupé - plusieurs factures liées */
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-indigo-700">
                  <FileText className="w-4 h-4" />
                  <span className="font-medium text-sm">Paiement groupé</span>
                </div>
                <Badge className="bg-indigo-100 text-indigo-700">
                  {transaction.factures_liees.length} factures
                </Badge>
              </div>
              
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {transaction.factures_liees.map((fac, idx) => (
                  <div 
                    key={fac.numero || idx}
                    className="p-2 bg-indigo-50 rounded border border-indigo-200"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => onViewPdf?.(fac.numero, isCredit ? 'vente' : 'achat')}
                          className="text-blue-600 text-sm font-medium hover:underline flex items-center gap-1"
                        >
                          <FileText className="w-3 h-3" />
                          {fac.numero}
                          <Eye className="w-3 h-3 opacity-50" />
                        </button>
                        <p className="text-xs text-slate-600 truncate">{fac.client || '-'}</p>
                      </div>
                      <span className="font-bold text-sm">{formatCurrency(fac.montant)}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="pt-2 border-t border-indigo-200 flex justify-between">
                <span className="text-sm text-slate-600">Total factures</span>
                <span className="font-bold text-indigo-700">
                  {formatCurrency(transaction.factures_liees.reduce((sum, f) => sum + (f.montant || 0), 0))}
                </span>
              </div>
            </div>
          ) : transaction.libelle_divers ? (
            /* Transaction classée comme diverse/traitée */
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
              <p className="text-purple-700 font-medium">{transaction.libelle_divers}</p>
              <p className="text-xs text-slate-500 mt-1">Classé comme traité</p>
            </div>
          ) : isRemSdd ? (
            /* Transaction REM SDD sans remise importée */
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-orange-800">Remise SEPA non importée</p>
                  <p className="text-sm text-orange-700 mt-1">
                    Cette transaction "REM SDD" correspond à un regroupement de prélèvements SEPA.
                  </p>
                  <p className="text-sm text-slate-600 mt-2">
                    Pour voir le détail des clients et factures, importez le PDF de remise correspondant dans <strong>"Remises SEPA"</strong>.
                  </p>
                  <div className="mt-3 p-2 bg-white rounded border border-orange-200">
                    <p className="text-xs text-slate-500">Montant à rechercher dans le PDF :</p>
                    <p className="font-bold text-lg text-green-600">{formatCurrency(transaction.amount)}</p>
                    <p className="text-xs text-slate-500">Date : {formatDate(transaction.date)}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : suggestions.length > 0 ? (
            /* Suggestions de factures - Validation en 1 clic */
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-700">
                <Sparkles className="w-4 h-4" />
                <span className="font-medium text-sm">{suggestions.length} facture{suggestions.length > 1 ? 's' : ''} suggérée{suggestions.length > 1 ? 's' : ''}</span>
              </div>
              
              {suggestions.map((fac, idx) => (
                <div 
                  key={fac.numero || idx}
                  className={`p-3 rounded-lg border ${
                    fac.isProbable 
                      ? 'bg-green-50 border-green-300' 
                      : 'bg-yellow-50 border-yellow-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onViewPdf?.(fac.numero, isCredit ? 'vente' : 'achat')}
                          className="font-medium text-blue-600 hover:underline text-sm flex items-center gap-1"
                        >
                          <FileText className="w-3 h-3" />
                          {fac.numero}
                          <Eye className="w-3 h-3 opacity-50" />
                        </button>
                        {fac.isProbable && (
                          <span className="bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
                            ★ Probable
                          </span>
                        )}
                        {!fac.isProbable && fac.isSameMontant && (
                          <span className="bg-yellow-100 text-yellow-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
                            Même montant
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-600 mt-1 truncate">
                        {fac.client?.nom || fac.client || fac.fournisseur || '-'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDate(fac.date)} • {formatCurrency(fac.total_ttc)}
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700 h-8"
                      onClick={() => handleValidate(fac)}
                      disabled={validating === fac.numero}
                    >
                      {validating === fac.numero ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Valider
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Zone drag & drop style Pennylane */
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-blue-300 transition-colors">
              <div className="w-12 h-12 mx-auto mb-3 bg-blue-50 rounded-lg flex items-center justify-center">
                <Upload className="w-6 h-6 text-blue-400" />
              </div>
              <p className="text-sm text-slate-600">Aucune facture correspondante trouvée</p>
              <p className="text-sm text-slate-400">Cliquez sur "Chercher une facture" ci-dessous</p>
            </div>
          )}

          {/* Bouton chercher une facture */}
          {canMatch && (
            <Button 
              variant="outline" 
              className="w-full mt-4 border-slate-300"
              onClick={onOpenMatchModal}
            >
              <Search className="w-4 h-4 mr-2" />
              Chercher une facture importée
            </Button>
          )}

          {/* Boutons d'action pour transactions rapprochées */}
          {isRapprochee && (
            <div className="flex gap-2 mt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={onOpenMatchModal}
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter factures
              </Button>
              <Button 
                variant="outline" 
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={onUnlink}
              >
                <Unlink className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TransactionDetailSheet;
